// clientLog ships structured frontend log records to POST /api/v1/clientlogs
// so silent in-handler errors (the kind that previously only got a
// console.error) land in Seq alongside backend logs. The /api/v1/clientlogs
// endpoint requires auth — pre-login records are stashed in a localStorage
// ring buffer and drained on the first setToken(non-null).
//
// Why a plain fetch rather than apiFetch:
//   - apiFetch's failure path calls Sentry.captureException, which is fine
//     in general — but if we used it here, a failed clientLog POST would
//     try to capture *itself* and we could get stuck retrying. A bare
//     fetch with a hard-coded failure ceiling avoids that loop.
//
// Why a module-level token reference instead of reading from context:
//   - global window.error / unhandledrejection listeners fire outside React,
//     so they can't useAuth(). main.tsx's AuthBridge effect calls setToken()
//     whenever auth state changes; clientLog uses whatever the latest value
//     is. The token is sessionStorage-backed already, so this isn't adding
//     new persistence.
//
// Resilience contract (added after a "where did my onboarding error go?"
// debugging session):
//   - Pre-auth records buffer to localStorage and drain on first token.
//   - The circuit breaker resets on every fresh non-null token so a prior
//     network blip doesn't permanently silence a fresh login.
//   - When the breaker trips, we capture a one-shot Sentry event so we know
//     clientLog itself is broken instead of just silently going dark.

import { config } from '../config';
import { captureException } from './sentry';

interface ClientLogError {
  name?: string;
  message?: string;
  stack?: string;
}

interface ClientLogOptions {
  /** Optional structured Error or Error-shaped object. */
  error?: unknown;
  /** Flat scalar attributes to attach (strings/numbers/bools). */
  fields?: Record<string, string | number | boolean | null>;
  /** Org slug context if the event is org-scoped. */
  orgSlug?: string;
}

type Level = 'error' | 'warn' | 'info';

interface BufferedRecord {
  level: Level;
  msg: string;
  opts: ClientLogOptions;
  t: number;
}

let currentToken: string | null = null;
// Circuit breaker — once we've had a few back-to-back failures we stop
// trying. Resets on token change so a fresh sign-in gets another chance.
// When the breaker trips we capture a one-shot Sentry event so we know
// clientLog itself is broken (otherwise: silent visibility loss).
let consecutiveFailures = 0;
const maxConsecutiveFailures = 3;
let disabled = false;

// Pre-auth ring buffer. clientLog can be called before AuthTokenBridge
// has handed us a token (early bootstrap errors, OIDC redirect race).
// Without a buffer those errors only land in console — they never reach
// Seq. We hold up to maxBufferedRecords and drain on the first token.
const maxBufferedRecords = 50;
const bufferKey = 'sportadmin.clientLog.pendingV1';
let inMemoryBuffer: BufferedRecord[] = loadBufferedFromStorage();

function loadBufferedFromStorage(): BufferedRecord[] {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(bufferKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((r): r is BufferedRecord => {
        if (!r || typeof r !== 'object') return false;
        const rec = r as Record<string, unknown>;
        return (
          (rec['level'] === 'error' || rec['level'] === 'warn' || rec['level'] === 'info') &&
          typeof rec['msg'] === 'string' &&
          typeof rec['t'] === 'number'
        );
      })
      .slice(0, maxBufferedRecords);
  } catch {
    return [];
  }
}

function persistBuffer(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (inMemoryBuffer.length === 0) {
      window.localStorage.removeItem(bufferKey);
      return;
    }
    window.localStorage.setItem(bufferKey, JSON.stringify(inMemoryBuffer));
  } catch {
    // Quota exceeded or storage disabled — non-fatal.
  }
}

// Truncate stack and message lengths client-side to match server limits —
// keeps payloads small and avoids a 413 + lost log line.
const maxMsg = 512;
const maxErrMessage = 1024;
const maxErrStack = 4096;

/**
 * setToken updates the bearer token used by future clientLog calls.
 * Wired by an effect in main.tsx that watches auth.user?.access_token.
 *
 * Token transitions are also recovery points:
 *   - on a fresh non-null token (re-login, refresh), reset the circuit
 *     breaker so a previous network blip doesn't permanently silence us
 *   - if we have buffered pre-auth records, drain them now
 */
export function setToken(token: string | null): void {
  const wasNull = currentToken == null;
  currentToken = token;
  if (token != null) {
    if (disabled || consecutiveFailures > 0) {
      disabled = false;
      consecutiveFailures = 0;
    }
    if (wasNull && inMemoryBuffer.length > 0) {
      void drainBuffer();
    }
  }
}

async function drainBuffer(): Promise<void> {
  // Snapshot + clear before sending so concurrent additions don't get
  // double-sent. Failures go back into the buffer head-first so we keep
  // chronological order on the next attempt.
  const pending = inMemoryBuffer;
  inMemoryBuffer = [];
  persistBuffer();
  const failed: BufferedRecord[] = [];
  for (const rec of pending) {
    const ok = await postOne(rec.level, rec.msg, rec.opts, { fromBuffer: true });
    if (!ok) failed.push(rec);
  }
  if (failed.length > 0) {
    inMemoryBuffer = [...failed, ...inMemoryBuffer].slice(0, maxBufferedRecords);
    persistBuffer();
  }
}

function bufferRecord(level: Level, msg: string, opts: ClientLogOptions): void {
  inMemoryBuffer.push({ level, msg, opts: stripUnserializable(opts), t: Date.now() });
  if (inMemoryBuffer.length > maxBufferedRecords) {
    // Drop oldest. Newer pre-auth context is usually more useful than
    // whatever fired during the very first paint.
    inMemoryBuffer.splice(0, inMemoryBuffer.length - maxBufferedRecords);
  }
  persistBuffer();
}

function stripUnserializable(opts: ClientLogOptions): ClientLogOptions {
  // shapeError() returns a plain object, so the error survives JSON.
  // Reshape eagerly so we don't try to JSON.stringify a thrown Error
  // (which would lose stack on most browsers).
  const out: ClientLogOptions = {};
  if (opts.error !== undefined) {
    const shaped = shapeError(opts.error);
    if (shaped) out.error = shaped;
  }
  if (opts.fields) out.fields = opts.fields;
  if (opts.orgSlug) out.orgSlug = opts.orgSlug;
  return out;
}

function shapeError(input: unknown): ClientLogError | undefined {
  if (input == null) return undefined;
  if (input instanceof Error) {
    const out: ClientLogError = {
      name: input.name,
      message: truncate(input.message, maxErrMessage),
    };
    if (input.stack) out.stack = truncate(input.stack, maxErrStack);
    return out;
  }
  if (typeof input === 'object') {
    const e = input as { name?: unknown; message?: unknown; stack?: unknown };
    const out: ClientLogError = {};
    if (typeof e.name === 'string') out.name = e.name;
    if (typeof e.message === 'string') out.message = truncate(e.message, maxErrMessage);
    if (typeof e.stack === 'string') out.stack = truncate(e.stack, maxErrStack);
    return out;
  }
  return { message: truncate(String(input), maxErrMessage) };
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max);
}

async function send(level: Level, msg: string, opts: ClientLogOptions = {}): Promise<void> {
  // Always mirror to the browser console so the developer still sees it
  // during local dev — the network bridge is additive, not a replacement.
  const consoleArgs: unknown[] = [`[clientLog:${level}]`, msg];
  if (opts.error !== undefined) consoleArgs.push(opts.error);
  if (opts.fields !== undefined) consoleArgs.push(opts.fields);
  switch (level) {
    case 'error':
      console.error(...consoleArgs);
      break;
    case 'warn':
      console.warn(...consoleArgs);
      break;
    default:
      console.info(...consoleArgs);
  }

  if (disabled) return;
  if (!currentToken) {
    // Pre-auth — Sentry covers these IF its DSN is wired AND the error
    // ever bubbles. clientLog calls from try/catch never bubble, so
    // without this buffer they'd be invisible. Stash and drain on
    // the first setToken(non-null).
    bufferRecord(level, msg, opts);
    return;
  }

  await postOne(level, msg, opts, { fromBuffer: false });
}

interface PostMeta {
  fromBuffer: boolean;
}

async function postOne(
  level: Level,
  msg: string,
  opts: ClientLogOptions,
  meta: PostMeta,
): Promise<boolean> {
  if (disabled) return false;
  if (!currentToken) return false;

  const body = {
    level,
    msg: truncate(msg, maxMsg),
    error: shapeError(opts.error),
    fields: opts.fields,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    org_slug: opts.orgSlug,
  };

  let ok = false;
  try {
    const res = await fetch(`${config.apiURL}/api/v1/clientlogs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentToken}`,
      },
      body: JSON.stringify(body),
      // keepalive lets the request survive a page unload (e.g. an error
      // during navigation). Capped at 64 KiB by the spec which is far
      // larger than our payload ceiling.
      keepalive: true,
    });
    if (!res.ok && res.status !== 401) {
      consecutiveFailures += 1;
    } else {
      consecutiveFailures = 0;
      ok = true;
    }
  } catch {
    consecutiveFailures += 1;
  }
  if (consecutiveFailures >= maxConsecutiveFailures && !disabled) {
    disabled = true;
    console.warn('[clientLog] disabled after repeated failures — falling back to console only');
    // One-shot Sentry alert so flying-blind sessions are still findable
    // somewhere. Without this, the only signal that clientLog is broken
    // is the absence of expected logs in Seq — which is exactly the
    // class of failure the user reported.
    captureException(
      new Error(
        `clientLog disabled after ${consecutiveFailures} consecutive POST failures` +
          (meta.fromBuffer ? ' (during buffer drain)' : ''),
      ),
    );
  }
  return ok;
}

export const clientLog = {
  error(msg: string, opts: ClientLogOptions = {}): void {
    void send('error', msg, opts);
  },
  warn(msg: string, opts: ClientLogOptions = {}): void {
    void send('warn', msg, opts);
  },
  info(msg: string, opts: ClientLogOptions = {}): void {
    void send('info', msg, opts);
  },
};

/**
 * installGlobalErrorHandlers attaches window-level listeners that forward
 * unhandled errors and promise rejections to clientLog. Idempotent — safe
 * to call from main.tsx during early boot, before auth resolves; events
 * that fire pre-auth simply hit the console path (no token, no POST).
 */
let installed = false;
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (ev: ErrorEvent) => {
    // ev.error is sometimes null (e.g. cross-origin scripts) — fall back
    // to the message string so we still ship something usable.
    clientLog.error(ev.message || 'window.error', {
      error: ev.error ?? { message: ev.message },
      fields: {
        source: ev.filename || '',
        line: ev.lineno || 0,
        col: ev.colno || 0,
      },
    });
  });

  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    const reason = ev.reason;
    const msg =
      reason instanceof Error
        ? reason.message || 'unhandledrejection'
        : typeof reason === 'string'
          ? reason
          : 'unhandledrejection';
    clientLog.error(truncate(msg, maxMsg), { error: reason });
  });
}
